/**
 * SmartChoiceDemo - Interactive testing page for SmartChoiceWidget
 *
 * Access via:
 * - http://localhost:3000/smart-choice-widget (locally)
 * - https://findcare-<ENV>.uhc.com/smart-choice-widget
 *
 *
 * This component provides a storybook-like interface to test various
 * configurations of the Smart Choice Widget before CEX integration.
 *
 * Features:
 * - Persistent settings (saved to localStorage)
 * - Mock recommendation preferences state presets
 * - Real-time widget preview
 * - Apply mock preferences via GraphQL API
 */
import { useForm } from '@abyss/web/hooks/useForm';
import { Accordion } from '@abyss/web/ui/Accordion';
import { Button } from '@abyss/web/ui/Button';
import { FormProvider } from '@abyss/web/ui/FormProvider';
import { IconMaterial } from '@abyss/web/ui/IconMaterial';
import { Skeleton } from '@abyss/web/ui/Skeleton';
import { TextInput } from '@abyss/web/ui/TextInput';
import { Tooltip } from '@abyss/web/ui/Tooltip';
import { CareCategories } from 'parcels/src/common/Constants';
import {
  SMART_CHOICE_EVENTS,
  SmartChoicePreferencesEventDetail,
  SmartChoiceWidgetApp,
} from 'parcels/src/frontends/SmartChoiceWidget';
import { useHsidUuid } from 'parcels/src/hooks/useHsidUuid';
import { useSaveRecommendationPreferences } from 'parcels/src/hooks/useSaveRecommendationPreferencesStatus';
import { StoreKeys } from 'parcels/src/hooks/useStore/state';
import { useStore } from 'parcels/src/hooks/useStore/useStore';
import { useChipStore } from 'parcels/src/store/useChipStore';
import React, { useCallback, useEffect, useState } from 'react';

import {
  LoadingStateType,
  MockPreferences,
  defaultSettings,
  loadingStateConfigs,
  mockPreferenceDescriptions,
  mockPresets,
  widgetPropDescriptions,
} from './constants';
import {
  ButtonGroup,
  ClearLogButton,
  Container,
  ControlPanel,
  EventLogAction,
  EventLogEmpty,
  EventLogHeader,
  EventLogItem,
  EventLogList,
  EventLogPayload,
  EventLogSection,
  EventLogTimestamp,
  EventLogTitle,
  FormField,
  FormFieldLabel,
  MockStateBadge,
  MockStateBadgeContainer,
  MockStateDescription,
  MockStateSection,
  MockStateSubsection,
  MockStateSubsectionTitle,
  MockStateSummary,
  MockStateSummaryTitle,
  MockStateTitle,
  PersistenceIndicator,
  PresetButton,
  PresetButtonGroup,
  PropsDisplay,
  RadioGroupWrapper,
  Section,
  SectionTitle,
  SmallText,
  StyledDivider,
  StyledHeading,
  StyledRadio,
  StyledRadioGroup,
  StyledText,
  StyledToggleSwitch,
  ToggleGroup,
  WidgetContainer,
  WidgetWrapper,
} from './SmartChoiceDemo.styled';
import {
  buildPortalData,
  getPortalDefaults,
  loadSavedSettings,
  saveSettings,
} from './utils';

// InfoTooltip component using Abyss Tooltip with left positioning to avoid sidebar overflow
const InfoTooltip = ({ text }: { text: string }) => (
  <Tooltip align="center" asChild={false} content={text} position="left">
    <IconMaterial color="$core.color.turquoise.100" icon="info" size="$sm" />
  </Tooltip>
);

export const SmartChoiceDemo = () => {
  // Load initial state from localStorage
  const initialSettings = loadSavedSettings();

  // Initialize chip store with a valid value so coachmark can display
  // The SmartChoiceWidget checks chipValue to determine if coachmark should show
  const { setChipValue } = useChipStore();

  // Widget Props State
  // isSuperUser is always true in the demo - required for testing/demo environment
  // to ensure preferences are stored with the superUser suffix and don't affect real user data
  const isSuperUser = true;
  const [isFullScreenCompareSmartChoice, setIsFullScreenCompareSmartChoice] =
    useState<boolean>(initialSettings.isFullScreenCompareSmartChoice);
  const [isMixedResultsPage, setIsMixedResultsPage] = useState<boolean>(
    initialSettings.isMixedResultsPage
  );
  const [showChoosePCPHeader, setShowChoosePCPHeader] = useState<boolean>(
    initialSettings.showChoosePCPHeader
  );
  const [showMemberSelection, setShowMemberSelection] = useState<boolean>(
    initialSettings.showMemberSelection
  );
  const [memberId, setMemberId] = useState<string>(
    initialSettings.memberId || 'demo-test-user-00000000'
  );

  // Portal Configuration State
  const [portalName, setPortalName] = useState<string>(
    initialSettings.portalName
  );
  const [theme, setTheme] = useState<string>(initialSettings.theme);
  const [language, setLanguage] = useState<string>(initialSettings.language);
  const [basePath, setBasePath] = useState<string>(initialSettings.basePath);
  const [useFallbackLob, setUseFallbackLob] = useState<boolean>(
    initialSettings.useFallbackLob ?? false
  );

  // Mock Recommendation Preferences State - using useForm for form management
  const [mockPreferences, setMockPreferences] = useState<MockPreferences>(
    initialSettings.mockPreferences
  );

  // Initialize useForm for mock preferences with react-hook-form integration
  const mockPreferencesForm = useForm({
    defaultValues: initialSettings.mockPreferences,
  });

  // Widget key to force re-render
  const [widgetKey, setWidgetKey] = useState<number>(0);

  // Loading state simulation for debugging skeleton states
  const [simulatedLoadingState, setSimulatedLoadingState] =
    useState<LoadingStateType>('none');

  // Persistence indicator
  const [lastSaved, setLastSaved] = useState<string | null>(null);

  // State for tracking mock preferences API calls
  const [isApplyingPreferences, setIsApplyingPreferences] =
    useState<boolean>(false);
  const [preferencesApplyStatus, setPreferencesApplyStatus] = useState<
    string | null
  >(null);

  // State to track events received from the widget (demonstrates parent app integration)
  const [lastWidgetEvent, setLastWidgetEvent] = useState<{
    type: string;
    timestamp: string;
    source: string;
    preferences?: SmartChoicePreferencesEventDetail['preferences'];
  } | null>(null);

  // Event log to track all widget events (simulates Redux action log)
  const [eventLog, setEventLog] = useState<
    Array<{
      id: number;
      type: string;
      timestamp: string;
      source: string;
      action: string; // Redux-style action name
      payload: Record<string, unknown>;
    }>
  >([]);

  // Get user ID for preferences API
  // Priority: 1) memberId prop (from input field), 2) store hsidUuid, 3) fallback test UUID
  const hsidUuidFromStore = useHsidUuid();
  // Fallback test UUID - used when no other ID is available (demo/testing context)
  const TEST_USER_UUID = 'demo-test-user-00000000';
  // Use memberId from input if provided, otherwise try store, then fallback
  const effectiveUserId = memberId || hsidUuidFromStore || TEST_USER_UUID;

  // Get the store setter to clear recommendation preferences from Zustand store
  const setRecommendationPreferences = useStore(
    StoreKeys.SET_RECOMMENDATION_PREFERENCES
  );

  useEffect(() => {
    // Set chipValue to 'All' which is a valid category for displaying coachmark
    // This is necessary because shouldDisplayCoachMarkBasedOnChipValue() checks for valid chip values
    setChipValue(CareCategories.ALL);
  }, [setChipValue]);

  // Sync form values with mockPreferences state when form changes
  useEffect(() => {
    const subscription = mockPreferencesForm.watch((values) => {
      setMockPreferences(values as MockPreferences);
    });
    return () => subscription.unsubscribe();
  }, [mockPreferencesForm]);

  // Listen for Smart Choice widget events (demonstrates how CEX would integrate)
  // This is how a parent application like CEX would save preferences to their Redux store
  // Also used to sync the demo's toggle controls with the actual widget state
  useEffect(() => {
    const addEventToLog = (
      type: string,
      source: string,
      action: string,
      payload: Record<string, unknown>
    ) => {
      // Use timestamp + random suffix to ensure unique IDs even if multiple events fire simultaneously
      const uniqueId = Date.now() + Math.random();
      setEventLog((prev) => [
        {
          id: uniqueId,
          type,
          timestamp: new Date().toISOString(),
          source,
          action,
          payload,
        },
        ...prev.slice(0, 49), // Keep last 50 events
      ]);
    };

    const handlePreferencesLoaded = (
      e: CustomEvent<SmartChoicePreferencesEventDetail>
    ) => {
      const { preferences, timestamp, source } = e.detail;

      setLastWidgetEvent({
        type: 'PREFERENCES_LOADED',
        timestamp,
        source,
        preferences,
      });

      // Add to event log (simulates Redux action dispatch)
      addEventToLog(
        'PREFERENCES_LOADED',
        source,
        'smartChoice/preferencesLoaded',
        {
          preferences,
          userId: e.detail.userId,
          isSuperUser: e.detail.isSuperUser,
        }
      );

      // Sync the demo's toggle controls with the widget's preferences
      // This replaces the need for a separate API call in the demo
      const syncedPreferences: MockPreferences = {
        hasDismissedCoachMark: preferences.hasDismissedCoachMark === 'y',
        hasDismissedCoachMarkWeb: preferences.hasDismissedCoachMarkWeb === 'y',
        hasDismissedPopover: preferences.hasDismissedPopover === 'y',
        hasSetPreferences: preferences.hasSetPreferences === 'y',
        hasDefaultPreferences: preferences.hasDefaultPreferences === 'y',
      };
      setMockPreferences(syncedPreferences);
      mockPreferencesForm.reset(syncedPreferences);

      // In CEX, you would dispatch to Redux here:
      // dispatch(setSmartChoicePreferences(preferences));
    };

    const handlePreferencesSaved = (
      e: CustomEvent<SmartChoicePreferencesEventDetail>
    ) => {
      const { preferences, timestamp, source } = e.detail;

      setLastWidgetEvent({
        type: 'PREFERENCES_SAVED',
        timestamp,
        source,
        preferences,
      });

      // Add to event log (simulates Redux action dispatch)
      addEventToLog(
        'PREFERENCES_SAVED',
        source,
        'smartChoice/preferencesSaved',
        {
          preferences,
          userId: e.detail.userId,
          isSuperUser: e.detail.isSuperUser,
        }
      );

      // Sync the demo's toggle controls when preferences are saved
      const syncedPreferences: MockPreferences = {
        hasDismissedCoachMark: preferences.hasDismissedCoachMark === 'y',
        hasDismissedCoachMarkWeb: preferences.hasDismissedCoachMarkWeb === 'y',
        hasDismissedPopover: preferences.hasDismissedPopover === 'y',
        hasSetPreferences: preferences.hasSetPreferences === 'y',
        hasDefaultPreferences: preferences.hasDefaultPreferences === 'y',
      };
      setMockPreferences(syncedPreferences);
      mockPreferencesForm.reset(syncedPreferences);

      // In CEX, you would dispatch to Redux here:
      // dispatch(setSmartChoicePreferences(preferences));
    };

    document.addEventListener(
      SMART_CHOICE_EVENTS.PREFERENCES_LOADED,
      handlePreferencesLoaded as EventListener
    );
    document.addEventListener(
      SMART_CHOICE_EVENTS.PREFERENCES_SAVED,
      handlePreferencesSaved as EventListener
    );

    return () => {
      document.removeEventListener(
        SMART_CHOICE_EVENTS.PREFERENCES_LOADED,
        handlePreferencesLoaded as EventListener
      );
      document.removeEventListener(
        SMART_CHOICE_EVENTS.PREFERENCES_SAVED,
        handlePreferencesSaved as EventListener
      );
    };
  }, [mockPreferencesForm]);

  // Hook for saving recommendation preferences to backend
  const [saveResult, saveRecommendationPreferences] =
    useSaveRecommendationPreferences({
      handleError: () => {
        setIsApplyingPreferences(false);
        setPreferencesApplyStatus(
          '❌ Failed to apply preferences. Check console for details.'
        );
        setTimeout(() => setPreferencesApplyStatus(null), 5000);
      },
    });

  // Handle save success via useEffect to ensure we have access to the response data
  // This solves the UUID mismatch issue where GET might return a different record
  useEffect(() => {
    if (
      saveResult?.data?.saveRecommendationPreferencesStatus &&
      isApplyingPreferences
    ) {
      const savedData = saveResult.data.saveRecommendationPreferencesStatus;

      // Store the SAVE response directly in Zustand store
      // This ensures the widget uses the exact data we just saved
      setRecommendationPreferences(savedData);

      setIsApplyingPreferences(false);
      setPreferencesApplyStatus('✅ Preferences applied successfully!');

      // Refresh widget to reflect new preferences
      setWidgetKey((prev) => prev + 1);

      // Clear status after 3 seconds
      setTimeout(() => setPreferencesApplyStatus(null), 3000);
    }
  }, [saveResult, isApplyingPreferences, setRecommendationPreferences]);

  // Helper function to clear Smart Choice related session storage
  // This ensures the widget reads fresh state after applying preferences
  // Note: We don't clear Zustand store here - it will be updated with save response data
  const clearSmartChoiceSessionStorage = useCallback(() => {
    sessionStorage.removeItem('hasDismissedPopover');
    sessionStorage.removeItem('hasDismissedCoachMarkWeb');
    sessionStorage.removeItem('smartChoicePopoverDismissed');
  }, []);

  // Function to apply mock preferences via GraphQL API
  const applyMockPreferencesToBackend = useCallback(() => {
    setIsApplyingPreferences(true);
    setPreferencesApplyStatus('⏳ Applying preferences...');

    // Clear session storage so widget reads fresh state from API
    clearSmartChoiceSessionStorage();

    // Build uuid with superUser suffix if applicable (matches expected API format)
    const uuidWithSuffix = `${effectiveUserId}${
      isSuperUser ? '-superUser' : ''
    }`;

    // Base payload with state flags
    const basePayload = {
      // Include both uuid (with suffix) and userId (without suffix) for API compatibility
      uuid: uuidWithSuffix,
      userId: effectiveUserId,
      hasDismissedCoachMark: mockPreferences.hasDismissedCoachMark ? 'y' : 'n',
      hasDismissedCoachMarkWeb: mockPreferences.hasDismissedCoachMarkWeb
        ? 'y'
        : 'n',
      hasDismissedPopover: mockPreferences.hasDismissedPopover ? 'y' : 'n',
      hasSetPreferences: mockPreferences.hasSetPreferences ? 'y' : 'n',
      isSuperUser,
    };

    // When hasSetPreferences is 'y', the API requires all preference fields
    // Include default values for required preference fields
    const preferenceFields = mockPreferences.hasSetPreferences
      ? {
          evngApptAvbl: 'n', // Evening appointments available
          wkndApptAvbl: 'n', // Weekend appointments available
          onlineApptSchdlAvbl: 'n', // Online appointment scheduling available
          virtualApptAvbl: 'n', // Virtual appointments available
          distance: '', // Distance (empty = no preference)
          languagesSpoken: '', // Languages spoken (empty = no preference)
          providerGender: '', // Provider gender preference (empty = no preference)
          providerBoardCertStatus: 'n', // Board certification status
        }
      : {};

    const payload = {
      ...basePayload,
      ...preferenceFields,
    };

    saveRecommendationPreferences({
      variables: payload,
    });
  }, [
    mockPreferences,
    effectiveUserId,
    isSuperUser,
    saveRecommendationPreferences,
    clearSmartChoiceSessionStorage,
  ]);

  // Save settings to localStorage whenever they change
  useEffect(() => {
    const settings = {
      isSuperUser,
      isFullScreenCompareSmartChoice,
      isMixedResultsPage,
      showChoosePCPHeader,
      showMemberSelection,
      memberId,
      portalName,
      theme,
      language,
      basePath,
      useFallbackLob,
      mockPreferences,
    };
    const timestamp = saveSettings(settings);
    if (timestamp) {
      setLastSaved(timestamp);
    }
  }, [
    isSuperUser,
    isFullScreenCompareSmartChoice,
    isMixedResultsPage,
    showChoosePCPHeader,
    showMemberSelection,
    memberId,
    portalName,
    theme,
    language,
    basePath,
    useFallbackLob,
    mockPreferences,
  ]);

  // Build the current props object for display
  const getCurrentProps = useCallback(
    () => ({
      'is-super-user': isSuperUser,
      'is-full-screen-compare-smart-choice': isFullScreenCompareSmartChoice,
      'is-mixed-results-page': isMixedResultsPage,
      'show-choose-pcp-header': showChoosePCPHeader,
      'show-member-selection': showMemberSelection,
      'member-id': memberId || undefined,
      'portal-data': buildPortalData(
        portalName,
        basePath,
        theme,
        language,
        useFallbackLob
      ),
      'mock-preferences': mockPreferences,
    }),
    [
      isSuperUser,
      isFullScreenCompareSmartChoice,
      isMixedResultsPage,
      showChoosePCPHeader,
      showMemberSelection,
      memberId,
      portalName,
      basePath,
      theme,
      language,
      mockPreferences,
    ]
  );

  // Apply a mock preset (updates local state and optionally calls backend)
  const applyPreset = (presetKey: string, applyToBackend: boolean = true) => {
    const preset = mockPresets[presetKey];
    if (preset) {
      setMockPreferences(preset.preferences);
      mockPreferencesForm.reset(preset.preferences);

      // Clear session storage so widget reads fresh state from API
      clearSmartChoiceSessionStorage();

      // Apply to backend if requested
      if (applyToBackend) {
        setIsApplyingPreferences(true);
        setPreferencesApplyStatus('⏳ Applying preset preferences...');

        // Build uuid with superUser suffix if applicable
        const uuidWithSuffix = `${effectiveUserId}${
          isSuperUser ? '-superUser' : ''
        }`;

        // Base payload with state flags
        const basePayload = {
          uuid: uuidWithSuffix,
          userId: effectiveUserId,
          hasDismissedCoachMark: preset.preferences.hasDismissedCoachMark
            ? 'y'
            : 'n',
          hasDismissedCoachMarkWeb: preset.preferences.hasDismissedCoachMarkWeb
            ? 'y'
            : 'n',
          hasDismissedPopover: preset.preferences.hasDismissedPopover
            ? 'y'
            : 'n',
          hasSetPreferences: preset.preferences.hasSetPreferences ? 'y' : 'n',
          isSuperUser,
        };

        // When hasSetPreferences is 'y', include required preference fields
        const preferenceFields = preset.preferences.hasSetPreferences
          ? {
              evngApptAvbl: 'n',
              wkndApptAvbl: 'n',
              onlineApptSchdlAvbl: 'n',
              virtualApptAvbl: 'n',
              distance: '',
              languagesSpoken: '',
              providerGender: '',
              providerBoardCertStatus: 'n',
            }
          : {};

        const payload = {
          ...basePayload,
          ...preferenceFields,
        };

        saveRecommendationPreferences({ variables: payload });
      } else {
        setWidgetKey((prev) => prev + 1);
      }
    }
  };

  // Handle portal name change with auto-defaults
  const handlePortalChange = (value: string) => {
    setPortalName(value);
    const defaults = getPortalDefaults(value);
    setTheme(defaults.theme);
    setBasePath(defaults.basePath);
    setUseFallbackLob(defaults.useFallbackLob);
    // Force widget to re-render with new portal configuration
    setWidgetKey((prev) => prev + 1);
  };

  const portalData = buildPortalData(
    portalName,
    basePath,
    theme,
    language,
    useFallbackLob
  );

  return (
    <Container>
      {/* Control Panel - Left Side */}
      <ControlPanel>
        <StyledHeading offset={4}>Smart Choice Widget Demo</StyledHeading>
        <StyledText>
          Configure widget props and test different scenarios
        </StyledText>

        {/* Persistence Indicator */}
        {lastSaved && (
          <PersistenceIndicator>
            💾 Settings auto-saved at {lastSaved}
          </PersistenceIndicator>
        )}

        {/* Mock Recommendation Preferences Section */}
        <MockStateSection>
          <MockStateTitle>
            🧪 Mock Recommendation Preferences
            {lastWidgetEvent && ' ✓ Synced via Widget Events'}
          </MockStateTitle>
          <MockStateDescription>
            Configure user preference states and apply them to the backend via
            GraphQL API. Changes are saved locally automatically, but must be
            applied to affect the widget behavior.
          </MockStateDescription>
          {lastWidgetEvent && (
            <MockStateDescription>
              <strong>
                Preferences synced from widget for user: {effectiveUserId}
              </strong>
            </MockStateDescription>
          )}

          {/* Quick Presets Subsection */}
          <MockStateSubsection>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                marginBottom: '12px',
              }}
            >
              <MockStateSubsectionTitle css={{ marginBottom: 0 }}>
                ⚡ Quick Presets
              </MockStateSubsectionTitle>
              <InfoTooltip text="Click a preset to instantly apply common user states. These presets update the Individual Preference Controls below." />
            </div>
            <PresetButtonGroup>
              {Object.entries(mockPresets).map(([key, preset]) => (
                <PresetButton
                  key={key}
                  onClick={() => applyPreset(key)}
                  size="sm"
                  title={preset.description}
                  variant="outline"
                >
                  {preset.label}
                </PresetButton>
              ))}
            </PresetButtonGroup>
          </MockStateSubsection>

          {/* Individual Preference Controls Subsection */}
          <MockStateSubsection>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                marginBottom: '12px',
              }}
            >
              <MockStateSubsectionTitle css={{ marginBottom: 0 }}>
                🎛️ Individual Preference Controls
              </MockStateSubsectionTitle>
              <InfoTooltip text="Fine-tune individual preference flags. Use the buttons below to apply these settings to the backend or clear session storage." />
            </div>

            {/* Individual Mock Preference Toggles - using FormProvider for form management */}
            <FormProvider state={mockPreferencesForm}>
              <ToggleGroup>
                {(
                  Object.keys(defaultSettings.mockPreferences) as Array<
                    keyof MockPreferences
                  >
                ).map((key) => (
                  <StyledToggleSwitch
                    helper={mockPreferenceDescriptions[key]}
                    key={key}
                    label={key}
                    model={key}
                  />
                ))}
              </ToggleGroup>
            </FormProvider>

            {/* Apply Preferences Button */}
            <ButtonGroup style={{ marginTop: '16px', gap: '8px' }}>
              <Button
                data-testid="apply-preferences-button"
                isLoading={isApplyingPreferences}
                onClick={applyMockPreferencesToBackend}
                variant="solid"
              >
                📤 Apply Preferences to Backend
              </Button>
              <Button
                data-testid="clear-session-storage-button"
                onClick={() => {
                  clearSmartChoiceSessionStorage();
                  setWidgetKey((prev) => prev + 1);
                  setPreferencesApplyStatus(
                    '🧹 Session storage cleared! Widget refreshed.'
                  );
                  setTimeout(() => setPreferencesApplyStatus(null), 3000);
                }}
                variant="outline"
              >
                🧹 Clear Session Storage
              </Button>
            </ButtonGroup>

            {/* Status Indicator */}
            {preferencesApplyStatus && (
              <SmallText css={{ marginTop: '8px', fontWeight: 500 }}>
                {preferencesApplyStatus}
              </SmallText>
            )}
          </MockStateSubsection>

          <SmallText
            css={{ marginTop: '12px', color: '#6b7280', lineHeight: '1.5' }}
          >
            <strong>How it works:</strong>
            <br />
            • "Apply Preferences to Backend" calls the
            SaveRecommendationPreferencesStatus GraphQL mutation
            <br />
            • Session storage is automatically cleared when applying preferences
            <br />
            • The widget checks BOTH session storage AND API response for
            coachmark/popover visibility
            <br />• Use "Clear Session Storage" if the widget isn't reflecting
            API state correctly
          </SmallText>
        </MockStateSection>

        <StyledDivider />

        {/* Widget Props Section */}
        <Section>
          <SectionTitle>Widget Props</SectionTitle>

          <FormField>
            <StyledToggleSwitch
              data-testid="is-super-user-toggle"
              helper={widgetPropDescriptions.isSuperUser}
              isChecked={isSuperUser}
              isDisabled
              label="is-super-user"
            />
            <SmallText css={{ color: '#6b7280', marginTop: '4px' }}>
              🔒 Always enabled in demo environment. This ensures test
              preferences are stored separately (with &quot;-superUser&quot;
              UUID suffix) and don&apos;t affect real user data. In production,
              CEX controls this based on user roles.
            </SmallText>
          </FormField>

          <FormField>
            <StyledToggleSwitch
              data-testid="is-full-screen-toggle"
              helper={widgetPropDescriptions.isFullScreenCompareSmartChoice}
              isChecked={isFullScreenCompareSmartChoice}
              label="is-full-screen-compare-smart-choice"
              onChange={(e) =>
                setIsFullScreenCompareSmartChoice(e.target.checked)
              }
            />
            {isFullScreenCompareSmartChoice && (
              <SmallText css={{ color: '#dc2626', marginTop: '4px' }}>
                ⚠️ Widget content is hidden when this is enabled. This prop is
                designed for use within PSXHeader context where other components
                handle the full-screen compare display.
              </SmallText>
            )}
          </FormField>

          <FormField>
            <StyledToggleSwitch
              data-testid="is-mixed-results-toggle"
              helper={widgetPropDescriptions.isMixedResultsPage}
              isChecked={isMixedResultsPage}
              label="is-mixed-results-page"
              onChange={(e) => setIsMixedResultsPage(e.target.checked)}
            />
          </FormField>

          <FormField>
            <StyledToggleSwitch
              data-testid="show-pcp-header-toggle"
              helper={widgetPropDescriptions.showChoosePCPHeader}
              isChecked={showChoosePCPHeader}
              label="show-choose-pcp-header"
              onChange={(e) => setShowChoosePCPHeader(e.target.checked)}
            />
          </FormField>

          <FormField>
            <StyledToggleSwitch
              data-testid="show-member-selection-toggle"
              helper={widgetPropDescriptions.showMemberSelection}
              isChecked={showMemberSelection}
              label="show-member-selection"
              onChange={(e) => setShowMemberSelection(e.target.checked)}
            />
          </FormField>

          <FormField>
            <TextInput
              data-testid="member-id-input"
              label="member-id"
              onChange={(e) => setMemberId(e.target.value)}
              placeholder="e.g., 793cf228-35a6-4e29-acd5-681d37e72ee8"
              value={memberId}
            />
            <FormFieldLabel>{widgetPropDescriptions.memberId}</FormFieldLabel>
          </FormField>
        </Section>

        <StyledDivider />

        {/* Portal Configuration Section */}
        <Section>
          <SectionTitle>Portal Configuration</SectionTitle>

          <RadioGroupWrapper>
            <StyledRadioGroup
              data-testid="portal-name-radio"
              display="column"
              label="Portal Name"
              onChange={(e) => handlePortalChange(e.target.value)}
              value={portalName}
            >
              <StyledRadio label="UHC (Standard)" value="uhc" />
              <StyledRadio label="LAWW (Behavioral)" value="laww" />
              <StyledRadio label="UMR" value="umr" />
              <StyledRadio label="GEHA" value="geha" />
            </StyledRadioGroup>
          </RadioGroupWrapper>

          {/* Theme is determined by portal name - read-only display */}
          <FormField>
            <FormFieldLabel>Theme (determined by Portal)</FormFieldLabel>
            <StyledText>
              <strong>{theme === 'optum' ? 'Optum' : 'UHC'}</strong>
            </StyledText>
            <SmallText>
              Theme is automatically set based on portal selection. LAWW uses
              Optum theme; all others use UHC theme.
            </SmallText>
          </FormField>

          {/* Base Path is determined by portal name - read-only display */}
          <FormField>
            <FormFieldLabel>Base Path (determined by Portal)</FormFieldLabel>
            <StyledText>
              <strong>{basePath}</strong>
            </StyledText>
            <SmallText>
              Base path is automatically set based on portal selection: UHC →
              /connect, LAWW → /laww, UMR → /umr, GEHA → /geha
            </SmallText>
          </FormField>

          <RadioGroupWrapper>
            <StyledRadioGroup
              data-testid="language-radio"
              display="row"
              label="Language"
              onChange={(e) => setLanguage(e.target.value)}
              value={language}
            >
              <StyledRadio label="English" value="en-US" />
              <StyledRadio label="Spanish" value="es-US" />
            </StyledRadioGroup>
          </RadioGroupWrapper>

          {/* Fallback LOB option - shown for LAWW since OBH Lagoon data typically doesn't exist in dev */}
          {portalName === 'laww' && (
            <FormField>
              <StyledToggleSwitch
                data-testid="use-fallback-lob-toggle"
                helper="Uses ENI Lagoon data instead of OBH. This is enabled by default for LAWW because OBH Lagoon data typically doesn't exist in dev environments. The widget will use the Optum theme but load ENI configuration data."
                isChecked={useFallbackLob}
                label="Use Fallback LOB (ENI instead of OBH)"
                onChange={(e) => {
                  setUseFallbackLob(e.target.checked);
                  setWidgetKey((prev) => prev + 1);
                }}
              />
              {useFallbackLob && (
                <SmallText css={{ color: '#d97706', marginTop: '4px' }}>
                  ⚠️ Using ENI Lagoon data. Widget appearance reflects
                  LAWW/Optum theme, but feature flags come from ENI
                  configuration.
                </SmallText>
              )}
            </FormField>
          )}
        </Section>

        <StyledDivider />

        {/* Developer Tools - Collapsible Accordions */}
        <Section>
          <SectionTitle>Developer Tools</SectionTitle>
          <Accordion type="multiple">
            {/* Loading State Debugger */}
            <Accordion.Item value="loading-debugger">
              <Accordion.Trigger>
                <Accordion.Header>🔄 Loading State Debugger</Accordion.Header>
              </Accordion.Trigger>
              <Accordion.Content>
                <SmallText css={{ marginBottom: '12px', color: '#6b7280' }}>
                  Simulate loading states to debug skeleton appearances. Only
                  the preferences loading skeleton is shown for widgets.
                </SmallText>
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '6px',
                  }}
                >
                  {(Object.keys(loadingStateConfigs) as LoadingStateType[]).map(
                    (stateKey) => {
                      const config = loadingStateConfigs[stateKey];
                      const isActive = simulatedLoadingState === stateKey;
                      return (
                        <Button
                          key={stateKey}
                          onClick={() => setSimulatedLoadingState(stateKey)}
                          size="sm"
                          variant={isActive ? 'solid' : 'outline'}
                        >
                          {config.label}
                          {stateKey !== 'none' && (
                            <span
                              style={{
                                fontSize: '10px',
                                marginLeft: '8px',
                                opacity: 0.7,
                              }}
                            >
                              {config.skeletonWidth}×{config.skeletonHeight}px
                            </span>
                          )}
                        </Button>
                      );
                    }
                  )}
                </div>
                {simulatedLoadingState !== 'none' && (
                  <SmallText
                    css={{
                      marginTop: '10px',
                      padding: '8px',
                      backgroundColor: '#fffbeb',
                      borderRadius: '4px',
                      color: '#92400e',
                    }}
                  >
                    <strong>Source:</strong>{' '}
                    {loadingStateConfigs[simulatedLoadingState].source}
                  </SmallText>
                )}
              </Accordion.Content>
            </Accordion.Item>

            {/* Current Props */}
            <Accordion.Item value="current-props">
              <Accordion.Trigger>
                <Accordion.Header>Current Props (JSON)</Accordion.Header>
              </Accordion.Trigger>
              <Accordion.Content>
                <PropsDisplay>
                  {JSON.stringify(getCurrentProps(), null, 2)}
                </PropsDisplay>
              </Accordion.Content>
            </Accordion.Item>

            {/* CEX Integration Example */}
            <Accordion.Item value="cex-example">
              <Accordion.Trigger>
                <Accordion.Header>CEX Integration Example</Accordion.Header>
              </Accordion.Trigger>
              <Accordion.Content>
                <PropsDisplay>
                  {`<smart-choice-widget-app
  is-super-user="${isSuperUser}"
  is-full-screen-compare-smart-choice="${isFullScreenCompareSmartChoice}"
  is-mixed-results-page="${isMixedResultsPage}"
  show-choose-pcp-header="${showChoosePCPHeader}"
  show-member-selection="${showMemberSelection}"
  portal-data='${JSON.stringify(portalData)}'
/>`}
                </PropsDisplay>
              </Accordion.Content>
            </Accordion.Item>
          </Accordion>
        </Section>
      </ControlPanel>

      {/* Widget Display - Right Side */}
      <WidgetContainer>
        <StyledHeading offset={5}>Widget Preview</StyledHeading>
        <StyledText>
          isSuperUser: <strong>{isSuperUser ? 'true' : 'false'}</strong> |
          Portal: <strong>{portalName}</strong> | Theme:{' '}
          <strong>{theme}</strong>
        </StyledText>

        {/* Mock State Summary */}
        <MockStateSummary>
          <MockStateSummaryTitle>🧪 Active Mock State</MockStateSummaryTitle>
          <MockStateBadgeContainer>
            {(
              Object.entries(mockPreferences) as [
                keyof MockPreferences,
                boolean
              ][]
            ).map(([key, value]) => (
              <MockStateBadge active={value} key={key}>
                {key}: {value ? '✓' : '✗'}
              </MockStateBadge>
            ))}
            {/* Simulating Loading State indicator */}
            <MockStateBadge
              active={simulatedLoadingState !== 'none'}
              key="simulatingLoading"
            >
              simulatingLoading: {simulatedLoadingState !== 'none' ? '✓' : '✗'}
            </MockStateBadge>
          </MockStateBadgeContainer>
        </MockStateSummary>

        {/* Widget Event Indicator - demonstrates parent app listening to widget events */}
        {lastWidgetEvent && (
          <MockStateSummary>
            <MockStateSummaryTitle>
              📡 Last Widget Event (Parent App Integration Demo)
            </MockStateSummaryTitle>
            <MockStateBadgeContainer>
              <MockStateBadge
                active={lastWidgetEvent.type === 'PREFERENCES_LOADED'}
              >
                Type: {lastWidgetEvent.type}
              </MockStateBadge>
              <MockStateBadge active={lastWidgetEvent.source === 'api-save'}>
                Source: {lastWidgetEvent.source}
              </MockStateBadge>
              <MockStateBadge active={false}>
                Time: {new Date(lastWidgetEvent.timestamp).toLocaleTimeString()}
              </MockStateBadge>
            </MockStateBadgeContainer>
            <SmallText css={{ marginTop: '8px', color: '#6b7280' }}>
              This demonstrates how CEX can listen to widget events and save
              preferences to Redux. Check console for full event details.
            </SmallText>
          </MockStateSummary>
        )}

        <WidgetWrapper>
          {simulatedLoadingState !== 'none' ? (
            <Skeleton
              height={loadingStateConfigs[simulatedLoadingState].skeletonHeight}
              variant="rounded"
              width={loadingStateConfigs[simulatedLoadingState].skeletonWidth}
            />
          ) : (
            <SmartChoiceWidgetApp
              breadcrumbs={[]}
              flex-css={JSON.stringify({ gap: '0 10px' })}
              is-full-screen-compare-smart-choice={
                isFullScreenCompareSmartChoice
              }
              is-mixed-results-page={isMixedResultsPage}
              is-super-user={isSuperUser}
              key={widgetKey}
              member-id={memberId || undefined}
              portal-data={JSON.stringify(portalData)}
              show-choose-pcp-header={showChoosePCPHeader}
              show-member-selection={showMemberSelection}
            />
          )}
        </WidgetWrapper>

        {/* Event Log Section - Simulates Redux DevTools / Action Log */}
        <EventLogSection>
          <EventLogHeader>
            <EventLogTitle>
              📋 Widget Event Log (Redux Actions for CEX)
            </EventLogTitle>
            <ClearLogButton onClick={() => setEventLog([])}>
              Clear Log
            </ClearLogButton>
          </EventLogHeader>
          <SmallText
            css={{ color: '#9ca3af', marginBottom: '12px', display: 'block' }}
          >
            These events can be consumed by CEX to update its Redux store. Each
            event represents an action that would be dispatched.
          </SmallText>
          <EventLogList>
            {eventLog.length === 0 ? (
              <EventLogEmpty>
                No events yet. Interact with the widget to see events appear
                here.
              </EventLogEmpty>
            ) : (
              eventLog.map((event) => (
                <EventLogItem
                  eventType={
                    event.type as
                      | 'PREFERENCES_LOADED'
                      | 'PREFERENCES_SAVED'
                      | 'PREFERENCES_ERROR'
                  }
                  key={event.id}
                >
                  <EventLogAction>
                    {event.action}
                    <EventLogTimestamp>
                      {new Date(event.timestamp).toLocaleTimeString()}
                    </EventLogTimestamp>
                  </EventLogAction>
                  <EventLogPayload>
                    {JSON.stringify(event.payload, null, 2)}
                  </EventLogPayload>
                </EventLogItem>
              ))
            )}
          </EventLogList>
        </EventLogSection>
      </WidgetContainer>
    </Container>
  );
};

export default SmartChoiceDemo;
